// src/App.js

import React, { useState, useEffect, Component } from 'react';
import './App.css';
import './styles/Markdown.css';
import './styles/InlineCitations.css';
import './styles/Theme.css';
import './styles/ShareButton.css';
import Header from './components/Header';
import Footer from './components/Footer';
import NewsSection from './components/common/NewsSection';
import SectionHeader from './components/common/SectionHeader';
import SectionNav from './components/SectionNav';
import Authors from './components/Authors';
import { SectionProvider } from './context/SectionContext';
import { ThemeProvider } from './context/ThemeContext';
import { processCitations } from './utils/contentProcessor';
import { marked } from 'marked';
import SourcesList from './components/common/SourcesList';
import SchemaMarkup from './components/SchemaMarkup';
import { loadAnalytics } from './utils/analytics';

const DEBUG = process.env.NODE_ENV === 'development';

// Error Boundary to catch render crashes
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    if (process.env.NODE_ENV === 'development') {
      console.error('ErrorBoundary caught:', error, errorInfo);
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="error-container">
          <div className="error">Something went wrong loading the briefing.</div>
          <p>Please refresh the page to try again.</p>
          <button onClick={() => window.location.reload()} style={{ marginTop: '1rem', padding: '0.5rem 1rem', cursor: 'pointer' }}>
            Refresh Page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// Helper: fetch with timeout
const fetchWithTimeout = async (url, options = {}, timeout = 8000) => {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, { ...options, signal: controller.signal });
    clearTimeout(id);
    return response;
  } catch (error) {
    clearTimeout(id);
    if (error.name === 'AbortError') {
      throw new Error(`Request timed out after ${timeout}ms`);
    }
    throw error;
  }
};




// Default empty data structure to use while loading or if data isn't available
const DEFAULT_DATA = {
  date: new Date().toLocaleDateString(),
  'big-story': { content: '', sources: [] },
  'quick-hits': { content: '', sources: [] },
  business: { content: '', sources: [] },
  'world-affairs': { content: '', sources: [] },
  'us-policy': { content: '', sources: [] },
  'london-ottawa': { content: '', sources: [] },
  'on-this-day': { content: '', sources: [] },
  'business-breakdown': { content: '', sources: [] },
  sources: []
};

// Mapping between file names and component prop names
const sectionMapping = {
  'big-story': 'bigStory',
  'quick-hits': 'quickHits',
  'business': 'business',
  'world-affairs': 'worldAffairs',
  'us-policy': 'usPolicy',
  'london-ottawa': 'londonOttawa',
  'on-this-day': 'onThisDay',
  'business-breakdown': 'businessBreakdown'
};

function App() {
  const [newsData, setNewsData] = useState(DEFAULT_DATA);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState('home');

  // Handle hash-based routing
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash;
      if (hash === '#authors') {
        setCurrentPage('authors');
        window.scrollTo(0, 0);
      } else if (!hash || hash === '#' || (hash.startsWith('#') && !['#authors'].includes(hash))) {
        // Only switch to home if not navigating to a section anchor
        if (currentPage === 'authors') {
          setCurrentPage('home');
        }
      }
    };

    // Check initial hash
    handleHashChange();

    // Listen for hash changes
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [currentPage]);

  // Navigate back to home
  const navigateHome = () => {
    window.location.hash = '';
    setCurrentPage('home');
    window.scrollTo(0, 0);
  };

  // Function to properly render content - now uses section citations directly
  const renderContent = (content, sectionCitations) => {
    if (!content) return '';

    try {
      let processedContent = content;

      // Process citations using section's citation map { "1": "url", "2": "url", ... }
      if (window.processCitations) {
        processedContent = window.processCitations(processedContent, sectionCitations || {});
      }

      // Render as markdown
      return marked.parse(processedContent);
    } catch (error) {
      if (DEBUG) console.error('Error rendering content:', error);
      return content; // Return original content as fallback
    }
  };

  useEffect(() => {
    const fetchNewsData = async () => {
      try {
        // Get the base URL from environment variable
        const baseUrl = process.env.PUBLIC_URL || '';
        if (DEBUG) console.log('Using base URL:', baseUrl);
        
        // Initialize with default data structure
        const completeData = { ...DEFAULT_DATA };
        
        try {
          // First fetch the index file with PUBLIC_URL prefix (with timeout)
          const indexResponse = await fetchWithTimeout(`${baseUrl}/data/latest/index.json`, {}, 8000);
          
          if (!indexResponse.ok) {
            if (DEBUG) console.warn(`Warning: Failed to fetch index.json: ${indexResponse.statusText}`);
          } else {
            const indexData = await indexResponse.json();
            if (DEBUG) console.log('Successfully fetched index data:', indexData);
            
            // Update date from index
            if (indexData.date) {
              completeData.date = indexData.date;
            }
            
            // If sections exist in the index, try to fetch them
            if (indexData.sections && Array.isArray(indexData.sections)) {
              if (DEBUG) console.log('Fetching sections in parallel:', indexData.sections);

              // Fetch all sections AND sources in parallel for maximum speed
              const fetchPromises = indexData.sections.map(async (sectionName) => {
                try {
                  const sectionUrl = `${baseUrl}/data/latest/${sectionName}.json`;
                  const response = await fetchWithTimeout(sectionUrl, {}, 8000);

                  if (!response.ok) {
                    if (DEBUG) console.warn(`Warning: Failed to fetch section ${sectionName}: ${response.statusText}`);
                    return null;
                  }

                  const sectionData = await response.json();
                  const propName = sectionMapping[sectionName] || sectionName;

                  if (DEBUG) console.log(`Successfully loaded section ${sectionName} as ${propName}`);
                  return { propName, data: sectionData };
                } catch (sectionError) {
                  if (DEBUG) console.warn(`Error fetching section ${sectionName}:`, sectionError);
                  return null;
                }
              });

              // Add sources fetch to the parallel batch
              const sourcesFetch = fetchWithTimeout(`${baseUrl}/data/latest/sources.json`, {}, 8000)
                .then(async (response) => {
                  if (response.ok) {
                    const sourcesData = await response.json();
                    return { type: 'sources', data: sourcesData };
                  }
                  return null;
                })
                .catch((err) => {
                  if (DEBUG) console.warn("Error fetching sources data:", err);
                  return null;
                });

              // Wait for all fetches to complete in parallel
              const results = await Promise.all([...fetchPromises, sourcesFetch]);

              // Process results
              results.forEach((result) => {
                if (!result) return;
                if (result.type === 'sources' && result.data?.articles) {
                  // New article-centric format
                  completeData.articles = result.data.articles;
                } else if (result.propName) {
                  completeData[result.propName] = result.data;
                }
              });
            } else {
              if (DEBUG) console.warn('No sections found in index data');
            }
          }
        } catch (fetchError) {
          if (DEBUG) console.error('Error fetching data:', fetchError);
        }
        
        // Ensure articles array exists (even if empty)
        if (!completeData.articles) {
          completeData.articles = [];
        }

        setNewsData(completeData);
        setLoading(false);
        
      } catch (err) {
        if (DEBUG) console.error('Error processing news data:', err);
        setError(`Failed to load news data. Please refresh the page.`);
        setLoading(false);
      }
    };

    fetchNewsData();
    
    // Make processCitations available globally - now uses section citations directly
    window.processCitations = (content, sectionCitations) => {
      // sectionCitations is { "1": "url", "2": "url", ... } from section.citations
      return processCitations(content, sectionCitations);
    };
    
    // Set up citation click handler
    const handleCitationClick = (event) => {
      if (event.target.classList.contains('inline-citation')) {
        const url = event.target.getAttribute('data-source-url');
        if (url) {
          window.open(url, '_blank', 'noopener,noreferrer');
        }
      }
    };

    document.addEventListener('click', handleCitationClick);

    // Rich tooltip for citations - shows favicon + publication + article title
    let tooltipEl = null;

    const getTitleFromUrl = (url) => {
      try {
        const urlObj = new URL(url);
        const path = urlObj.pathname;
        // Extract the last meaningful segment from the path
        const segments = path.split('/').filter(s => s && s.length > 1);
        if (segments.length === 0) return null;

        let titleSlug = segments[segments.length - 1];
        // Remove file extensions and query params
        titleSlug = titleSlug.replace(/\.(html?|php|aspx?)$/i, '');
        // Remove common ID patterns at the end
        titleSlug = titleSlug.replace(/-\d{5,}$/, '');
        // Convert slug to title case
        return titleSlug
          .replace(/[-_]/g, ' ')
          .replace(/\b\w/g, c => c.toUpperCase())
          .trim();
      } catch {
        return null;
      }
    };

    const showTooltip = (citation, event) => {
      const url = citation.getAttribute('data-source-url');
      const name = citation.getAttribute('data-source-name');
      const faviconUrl = citation.getAttribute('data-favicon-url');

      if (!url || !name) return;

      const title = getTitleFromUrl(url);

      // Create tooltip element
      tooltipEl = document.createElement('div');
      tooltipEl.className = 'citation-rich-tooltip';
      tooltipEl.innerHTML = `
        <img src="${faviconUrl}" alt="" width="16" height="16" class="tooltip-favicon" />
        <span class="tooltip-publication">${name}</span>
        ${title ? `<span class="tooltip-separator">—</span><span class="tooltip-title">${title}</span>` : ''}
      `;

      document.body.appendChild(tooltipEl);

      // Position above the citation
      const rect = citation.getBoundingClientRect();
      const tooltipRect = tooltipEl.getBoundingClientRect();

      let left = rect.left + rect.width / 2 - tooltipRect.width / 2;
      // Keep within viewport
      left = Math.max(8, Math.min(left, window.innerWidth - tooltipRect.width - 8));

      tooltipEl.style.left = `${left}px`;
      tooltipEl.style.top = `${rect.top + window.scrollY - tooltipRect.height - 8}px`;

      // Animate in
      requestAnimationFrame(() => {
        tooltipEl.classList.add('visible');
      });
    };

    const hideTooltip = () => {
      if (tooltipEl) {
        tooltipEl.remove();
        tooltipEl = null;
      }
    };

    let activeCitation = null;

    const handleMouseMove = (event) => {
      const citation = event.target.closest('.inline-citation[data-source-url]');
      const isOverTooltip = tooltipEl && (event.target === tooltipEl || tooltipEl.contains(event.target));

      if (citation) {
        if (citation !== activeCitation) {
          // Entering a new citation
          hideTooltip();
          activeCitation = citation;
          showTooltip(citation, event);
        }
      } else if (isOverTooltip) {
        // Over the tooltip - keep it open
      } else if (activeCitation) {
        // Not over citation or tooltip - hide
        hideTooltip();
        activeCitation = null;
      }
    };

    const handleScroll = () => {
      if (activeCitation) {
        hideTooltip();
        activeCitation = null;
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('scroll', handleScroll, true);

    // Clean up event listeners when component unmounts
    return () => {
      document.removeEventListener('click', handleCitationClick);
      document.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('scroll', handleScroll, true);
      hideTooltip();
    };
  }, []);

  // Load analytics after initial render (non-blocking)
  useEffect(() => {
    if (!loading) {
      loadAnalytics();
    }
  }, [loading]);

  if (loading) {
    return (
      <ThemeProvider>
        <SchemaMarkup date={DEFAULT_DATA.date} />
        <div className="loading-container">
          <div className="loading">Loading today's briefing...</div>
          <p>If this takes too long, data may not be available yet.</p>
        </div>
      </ThemeProvider>
    );
  }

  if (error) {
    return (
      <ThemeProvider>
        <SchemaMarkup date={DEFAULT_DATA.date} />
        <div className="error-container">
          <div className="error">{error}</div>
          <p>Please check the browser console for more details.</p>
        </div>
      </ThemeProvider>
    );
  }

  // Make sure each component gets valid data structure even if something is missing
  const safeData = {
    date: newsData.date || DEFAULT_DATA.date,
    bigStory: newsData.bigStory || DEFAULT_DATA['big-story'],
    quickHits: newsData.quickHits || DEFAULT_DATA['quick-hits'],
    business: newsData.business || DEFAULT_DATA.business,
    worldAffairs: newsData.worldAffairs || DEFAULT_DATA['world-affairs'],
    usPolicy: newsData.usPolicy || DEFAULT_DATA['us-policy'],
    londonOttawa: newsData.londonOttawa || DEFAULT_DATA['london-ottawa'],
    onThisDay: newsData.onThisDay || DEFAULT_DATA['on-this-day'],
    businessBreakdown: newsData.businessBreakdown || DEFAULT_DATA['business-breakdown'],
    articles: newsData.articles || []
  };

  // Render Authors page if on that route
  if (currentPage === 'authors') {
    return (
      <ThemeProvider>
        <div className="app">
          <Authors onBack={navigateHome} />
        </div>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider>
      <SchemaMarkup date={safeData.date} />
      <SectionProvider>
        <div className="app">
          <Header date={safeData.date} />

          {/* Section Navigation - horizontal bar */}
          <SectionNav />

          {/* Main Layout - Sidebar + Content */}
          <div className="main-wrapper">
            {/* Floating Sidebar - Quick Hits */}
            <aside className="floating-sidebar" id="quick-hits">
              <div className="sidebar-inner">
                <SectionHeader
                  title="Quick Hits"
                  sectionName="quick-hits"
                  shareable={false}
                />
                <div
                  className="quick-hits-content markdown-content"
                  dangerouslySetInnerHTML={{
                    __html: renderContent(safeData.quickHits.content, safeData.quickHits.citations || safeData.quickHits.articleUrls)
                  }}
                />
              </div>
            </aside>

            {/* Main Content Area */}
            <main className="main-content">
              {/* Big Story - Breaking News */}
              <section className="big-story-section" id="big-story">
                <SectionHeader
                  title="THE BIG STORY"
                  sectionName="big-story"
                  content={safeData.bigStory.content}
                  shareable={true}
                />
                {(() => {
                  const renderedContent = renderContent(safeData.bigStory.content, safeData.bigStory.citations || safeData.bigStory.articleUrls);
                  if (!renderedContent || renderedContent.trim() === '') {
                    return (
                      <div className="big-story-content markdown-content">
                        <p className="content-unavailable">Today's big story is currently unavailable. Please check back later.</p>
                      </div>
                    );
                  }
                  return (
                    <div
                      className="big-story-content markdown-content"
                      dangerouslySetInnerHTML={{ __html: renderedContent }}
                    />
                  );
                })()}
                {(safeData.bigStory.citations || safeData.bigStory.articleUrls) && Object.keys(safeData.bigStory.citations || safeData.bigStory.articleUrls || {}).length > 0 && (
                  <div className="big-story-sources">
                    <SourcesList
                      citations={safeData.bigStory.citations || safeData.bigStory.articleUrls}
                    />
                  </div>
                )}
              </section>

              {/* Two-Column Grid for other sections */}
              <div className="content-grid">
                <div className="content-column">
                  <NewsSection
                    title="WORLD AFFAIRS"
                    content={safeData.worldAffairs.content}
                    citations={safeData.worldAffairs.citations || safeData.worldAffairs.articleUrls}
                    sectionName="world-affairs"
                    className="newspaper-article"
                  />

                  <NewsSection
                    title="LONDON & OTTAWA"
                    content={safeData.londonOttawa.content}
                    citations={safeData.londonOttawa.citations || safeData.londonOttawa.articleUrls}
                    sectionName="london-ottawa"
                    className="newspaper-article"
                  />
                </div>

                <div className="content-column">
                  <NewsSection
                    title="US POLICY"
                    content={safeData.usPolicy.content}
                    citations={safeData.usPolicy.citations || safeData.usPolicy.articleUrls}
                    sectionName="us-policy"
                    className="newspaper-article"
                  />

                  <NewsSection
                    title="BUSINESS"
                    content={safeData.business.content}
                    citations={safeData.business.citations || safeData.business.articleUrls}
                    sectionName="business"
                    className="newspaper-article"
                  />
                </div>
              </div>

              {/* On This Day - Feature */}
              <section className="newspaper-article on-this-day-section" id="on-this-day">
                <SectionHeader
                  title="ON THIS DAY"
                  sectionName="on-this-day"
                  content={safeData.onThisDay.content}
                  shareable={true}
                />
                <div
                  className="on-this-day-content markdown-content"
                  dangerouslySetInnerHTML={{
                    __html: renderContent(safeData.onThisDay.content, safeData.onThisDay.citations || safeData.onThisDay.articleUrls)
                  }}
                />
                <SourcesList citations={safeData.onThisDay.citations || safeData.onThisDay.articleUrls} />
              </section>

              {/* Business Breakdown - Full Width */}
              <section className="business-breakdown-section" id="business-breakdown">
                <SectionHeader
                  title="BUSINESS BREAKDOWN"
                  sectionName="business-breakdown"
                  content={safeData.businessBreakdown.content}
                  shareable={true}
                />
                <div
                  className="business-breakdown-content markdown-content"
                  dangerouslySetInnerHTML={{
                    __html: renderContent(safeData.businessBreakdown.content, safeData.businessBreakdown.citations || safeData.businessBreakdown.articleUrls)
                  }}
                />
                <SourcesList citations={safeData.businessBreakdown.citations || safeData.businessBreakdown.articleUrls} />
              </section>
            </main>
          </div>

          <Footer
            articles={safeData.articles}
            date={safeData.date}
          />
        </div>
      </SectionProvider>
    </ThemeProvider>
  );
}

// Wrap App with ErrorBoundary
const AppWithErrorBoundary = () => (
  <ErrorBoundary>
    <App />
  </ErrorBoundary>
);

export default AppWithErrorBoundary;